<template>
  <div
    class="sidebar-wrapper"
    :class="{ 'mobile-sidebar-open': mobileSidebarOpen, 'mobile-sidebar-closed': !mobileSidebarOpen, 'is-retracted': isRetracted, 'is-swoop-brand': isSwoopBranded }"
  >
    <div v-if="mobileSidebarOpen" class="mobile-drawer-backdrop" @click="closeSidebar"></div>

    <aside @click.stop>
      <div class="sidebar-header">
        <div
          class="logo-area"
          :role="isCollapsedRail ? 'button' : undefined"
          :tabindex="isCollapsedRail ? 0 : undefined"
          :aria-label="isCollapsedRail ? 'Open menu' : undefined"
          @click="expandFromLogo"
          @keydown.enter="expandFromLogo"
          @keydown.space.prevent="expandFromLogo"
          @mouseenter="showTooltip('Open menu', $event.currentTarget)"
          @mouseleave="hideTooltip"
        >
          <SwoopLogo v-if="!isCollapsedRail && !signupLogoError" logoType="signup" @error="signupLogoError = true" />
          <SwoopMonogramLogo v-else-if="isSwoopBranded" class="swoop-monogram" aria-label="Swoop" viewBox="0 0 44 44" />
          <div v-else class="monogram-circle" :title="brandName">{{ brandInitial }}</div>
          <span v-if="isCollapsedRail" class="material-symbols-outlined logo-expand-icon" aria-hidden="true">left_panel_open</span>
        </div>
        <button
          class="toggle-btn"
          ref="toggleBtn"
          :aria-label="isRetracted ? 'Open sidebar' : 'Close sidebar'"
          @click="toggleRetracted"
          @mouseenter="showTooltip(isRetracted ? 'Open sidebar' : 'Close sidebar', $event.currentTarget, true)"
          @mouseleave="hideTooltip"
        >
          <span class="material-symbols-outlined toggle-icon">{{ toggleIcon }}</span>
        </button>
      </div>
      <div v-if="isCollapsedRail" class="sidebar-divider sidebar-divider--header"></div>

      <div class="menu">
        <template v-for="section in visibleSections">
          <div v-if="section.heading" :key="`heading-${section.heading}`" class="menu-section-heading">{{ section.heading }}</div>
          <SidebarCategory
            v-for="sidebarItem in section.items"
            :key="sidebarItem.label"
            :category="sidebarItem"
            :collapsed="isCollapsedRail"
            :mobile="isMobileViewport"
            @close-mobile-sidebar="closeSidebar"
            @item-hover="showItemTooltip"
            @item-leave="hideTooltip"
          />
        </template>
      </div>

      <div class="sidebar-footer">
        <transition name="user-menu-slide">
          <div v-if="userMenuOpen" class="user-menu-items">
            <button class="user-menu-item logout" aria-label="Sign out" @click="logout" @mouseenter="showTooltip('Sign out', $event.currentTarget)" @mouseleave="hideTooltip">
              <span class="material-symbols-outlined">logout</span>
              <span class="user-menu-label">Sign out</span>
            </button>
            <router-link
              v-if="!user?.isXeroRestrictedUser"
              :to="{ name: 'profile' }"
              class="user-menu-item"
              aria-label="My profile"
              @click.native="closeSidebar"
              @mouseenter.native="showTooltip('My profile', $event.currentTarget)"
              @mouseleave.native="hideTooltip"
            >
              <span class="material-symbols-outlined">person</span>
              <span class="user-menu-label">My profile</span>
            </router-link>
          </div>
        </transition>

        <div
          class="user-row"
          :class="{ 'has-active-route': isOnUserMenuRoute, 'is-menu-open': userMenuOpen }"
          role="button"
          tabindex="0"
          :aria-expanded="userMenuOpen"
          :aria-label="userMenuOpen ? 'Close user menu' : 'Open user menu'"
          :title="userDisplayName"
          @click="toggleUserMenu"
          @keydown.enter="toggleUserMenu"
          @keydown.space.prevent="toggleUserMenu"
          @mouseenter="showTooltip('Profile', $event.currentTarget)"
          @mouseleave="hideTooltip"
        >
          <div class="user-avatar">{{ userInitials }}</div>
          <span class="user-name">{{ userDisplayName }}</span>
          <span class="user-row-gear" aria-hidden="true">
            <span class="material-icons-outlined">settings</span>
          </span>
        </div>
      </div>
    </aside>

    <transition name="tooltip-fade">
      <div v-if="tooltipLabel" class="sidebar-tooltip" role="tooltip" :style="tooltipStyle">{{ tooltipLabel }}</div>
    </transition>

    <transition name="tooltip-fade">
      <div v-if="flyout" class="sidebar-flyout" :style="flyoutStyle" @mouseenter="keepFlyoutOpen" @mouseleave="hideFlyout">
        <p class="sidebar-flyout-heading">{{ flyout.label }}</p>
        <SidebarCategoryChild
          v-for="item in flyout.menuItems"
          :key="item.label"
          :label="item.label"
          :icon="item.icon"
          :routeName="item.routeName"
          :externalLink="item.externalLink"
          :productName="item.productName"
          @link-clicked="hideFlyout"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { hasValidRegistrationNumber } from '@/utils/utils'
import { authGetters, logoutRoutine } from '@/store/modules/auth/routines'
import { companyGetters, companySelectRoutine } from '@/store/modules/company/routines'
import { applicationsGetters, fetchMyApplications } from '@/store/modules/applications/routines'
import { apiDeckGetters } from '@/store/modules/apiDeck/routines'
import SidebarCategory from '@/components/layout/SidebarCategory.vue'
import SidebarCategoryChild from '@/components/layout/SidebarCategoryChild.vue'
import SwoopLogo from '@/components/SwoopLogo.vue'
import SwoopMonogramLogo from '@/assets/images/config/swoop-monogram-logo.svg'
import useWhitelabel from '@/composables/useWhitelabel'
import { getCompany } from '@/api/company'
import useSegmentAnalytics from '@/composables/segmentTracking'
import { isSwoopBrandedTenant } from '@/utils/tenant'

const CLOSE_MOBILE_SIDEBAR = 'close-mobile-sidebar'

const visibleItems = items =>
  items
    .filter(item => item.enabled)
    .map(item => (item.menuItems ? { ...item, menuItems: item.menuItems.filter(child => child.enabled) } : item))
    .filter(item => !item.menuItems || item.menuItems.length > 0)

export default {
  components: { SidebarCategory, SidebarCategoryChild, SwoopLogo, SwoopMonogramLogo },
  props: {
    mobileSidebarOpen: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const { config: globalConfig } = useWhitelabel('global')
    const { config: portalConfig } = useWhitelabel('portal')
    const { config: additionalProductsConfig } = useWhitelabel('additionalProducts')

    const { trackEvent } = useSegmentAnalytics()

    return { globalConfig, portalConfig, additionalProductsConfig, trackEvent }
  },
  data() {
    return {
      windowSize: window.innerWidth,
      isSoleTrader: false,
      fundingItems: [],
      hasCreditSafeReport: null,
      isRetracted: false,
      userMenuOpen: false,
      tooltipLabel: '',
      tooltipStyle: {},
      flyout: null,
      flyoutStyle: {},
      flyoutCloseTimer: null,
      signupLogoError: false
    }
  },
  mounted() {
    this.fetchCompany()
    if (this.isDealsStale) this.fetchDeals()
    window.addEventListener('resize', this.handleWindowResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleWindowResize)
    clearTimeout(this.flyoutCloseTimer)
    document.documentElement.style.removeProperty('--app-sidebar-width')
  },
  watch: {
    windowSize(value) {
      if (value >= 1024) {
        this.$emit(CLOSE_MOBILE_SIDEBAR)
      }
    },
    sidebarFlowWidth: {
      handler(width) {
        document.documentElement.style.setProperty('--app-sidebar-width', width)
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions({
      selectCompany: companySelectRoutine.TRIGGER,
      triggerLogout: logoutRoutine.TRIGGER
    }),
    ...mapActions('applications', {
      fetchDeals: fetchMyApplications.TRIGGER
    }),
    handleWindowResize() {
      this.windowSize = window.innerWidth
      if (this.tooltipLabel) this.hideTooltip()
    },
    linkClick() {
      this.$emit(CLOSE_MOBILE_SIDEBAR)
    },
    logout() {
      this.trackEvent('Logged Out', {
        reason: 'User logged out',
        userId: this.user?.userId,
        name: this.userDisplayName,
        email: this.user?.email
      })
      this.linkClick()
      this.triggerLogout()
    },
    closeSidebar() {
      this.$emit(CLOSE_MOBILE_SIDEBAR)
    },
    toggleRetracted() {
      this.hideTooltip()
      // On mobile the toggle acts as a close-drawer button instead of a retract/expand toggle
      if (this.mobileSidebarOpen) {
        this.$emit(CLOSE_MOBILE_SIDEBAR)
        return
      }
      this.isRetracted = !this.isRetracted
      if (this.isRetracted) this.userMenuOpen = false
    },
    expandFromLogo() {
      if (this.isCollapsedRail) this.toggleRetracted()
    },
    toggleUserMenu() {
      this.userMenuOpen = !this.userMenuOpen
    },
    showTooltip(label, el, always = false) {
      if (!el || !label || (!always && !this.isCollapsedRail)) return
      const rect = el.getBoundingClientRect()
      const aside = this.$el.querySelector('aside')
      this.tooltipStyle = {
        top: rect.top + rect.height / 2 + 'px',
        left: (aside ? aside.getBoundingClientRect().right : rect.right) + 14 + 'px'
      }
      this.tooltipLabel = label
    },
    showItemTooltip({ label, flyoutLabel, el, menuItems }) {
      if (this.isCollapsedRail && menuItems?.length) return this.showFlyout(flyoutLabel, el, menuItems)
      this.showTooltip(label, el)
    },
    showFlyout(label, el, menuItems) {
      clearTimeout(this.flyoutCloseTimer)
      const rect = el.getBoundingClientRect()
      const aside = this.$el.querySelector('aside')
      this.flyoutStyle = {
        top: rect.top - 16 + 'px',
        left: (aside ? aside.getBoundingClientRect().right : rect.right) + 8 + 'px'
      }
      this.flyout = { label, menuItems }
      this.tooltipLabel = ''
    },
    hideFlyout() {
      this.flyoutCloseTimer = setTimeout(() => {
        this.flyout = null
      }, 250)
    },
    keepFlyoutOpen() {
      clearTimeout(this.flyoutCloseTimer)
    },
    hideTooltip() {
      this.tooltipLabel = ''
      this.hideFlyout()
    },
    async fetchCompany() {
      try {
        const response = await getCompany(this.companyId)
        this.hasCreditSafeReport = response.data.hasCreditSafeReport
      } catch (error) {
        console.error('Error checking if credit safe report is available:', error)
      }
    }
  },
  computed: {
    ...mapGetters({
      company: companyGetters.COMPANY_CURRENT,
      companyId: companyGetters.COMPANY_ID,
      currentCompanyId: companyGetters.COMPANY_CURRENT_ID,
      user: authGetters.USER,
      apiDeckIntegration: apiDeckGetters.APIDECK_INTEGRATION
    }),
    ...mapGetters('applications', {
      applicationsNeedingAttention: applicationsGetters.ACTIONABLE_INFORMATION_REQUESTED_COUNT,
      isDealsStale: applicationsGetters.IS_STALE
    }),
    isSwoopBranded() {
      return isSwoopBrandedTenant(this.globalConfig?.tenantId)
    },
    isMobileViewport() {
      return this.windowSize < 1024
    },
    isCollapsedRail() {
      // isRetracted is a desktop-only rail collapse; on mobile the drawer is always expanded.
      return this.isRetracted && this.windowSize >= 1024
    },
    sidebarFlowWidth() {
      if (this.windowSize < 1024) return '0px'
      return this.isRetracted ? '72px' : '280px'
    },
    isAccountingSoftwareConnected() {
      return this.apiDeckIntegration?.status === 'connected'
    },
    toggleIcon() {
      if (this.mobileSidebarOpen) return 'close'
      return this.isRetracted ? 'left_panel_open' : 'left_panel_close'
    },
    brandName() {
      return this.globalConfig?.clientName || this.globalConfig?.tenantId || ''
    },
    isOnUserMenuRoute() {
      return this.$route?.name === 'profile'
    },
    brandInitial() {
      return (this.brandName.trim().charAt(0) || 'S').toUpperCase()
    },
    userDisplayName() {
      if (!this.user) return ''
      const first = this.user.firstName || ''
      const last = this.user.lastName || ''
      return `${first} ${last}`.trim() || this.user.email || ''
    },
    userInitials() {
      if (!this.user) return ''
      const first = this.user.firstName?.charAt(0) || ''
      const last = this.user.lastName?.charAt(0) || ''
      const initials = (first + last).toUpperCase()
      return initials || (this.user.email?.charAt(0).toUpperCase() ?? '')
    },
    visibleSections() {
      return this.sidebarSections.map(section => ({ ...section, items: visibleItems(section.items) })).filter(section => section.items.length > 0)
    },
    sidebarSections() {
      const { hasLoanProducts, hasGrantProducts, hasEquityProducts, countryCode } = this.globalConfig
      const {
        enableAccountingSoftwareIntegration,
        enableOpenBankingIntegration,
        enableIllionIntegration,
        enablePlaidIntegration,
        enableCashFlowForecasting,
        enableSavingsFeatures,
        enableBankingPage,
        enableUtilitiesPage,
        enableCreditHealthCheck,
        enableAIAssistant
      } = this.portalConfig

      const {
        enableFXPage,
        enableResearchAndDevelopmentPage,
        enableBusinessSavingsAccountsPage,
        enablePensionPage,
        enableBusinessInsurancePage,
        enableCreditCardPage,
        enableCreditScoreImprovementsPage,
        enableCreditsafeReportPage,
        enableBusinessCheckingAccountsPage
      } = this.additionalProductsConfig

      const { hasCreditSafeReport } = this

      if (this.user?.isXeroRestrictedUser) {
        return [
          {
            heading: null,
            items: [
              {
                icon: 'account_balance',
                label: 'Cash flow & forecasting',
                routeName: this.isAccountingSoftwareConnected ? 'cashflow-forecast' : 'cashflow-forecasting-integration',
                enabled: enableCashFlowForecasting
              },
              {
                label: 'Connected data',
                routeName: 'integrations',
                icon: 'integration_instructions',
                enabled: enableIllionIntegration || enableAccountingSoftwareIntegration || enableOpenBankingIntegration || enablePlaidIntegration
              }
            ]
          }
        ]
      } else {
        return [
          {
            heading: null,
            items: [
              {
                label: 'Home',
                icon: 'home',
                routeName: 'dashboard',
                enabled: true
              },
              {
                label: 'Assistant',
                icon: 'auto_awesome',
                routeName: 'chat',
                badge: 'BETA',
                enabled: enableAIAssistant
              }
              // we will need this again when we reinstate cashflow forecasting
              // {
              //   label: 'Cash flow & forecasting',
              //   icon: 'account_balance',
              //   routeName: this.isAccountingSoftwareConnected ? 'cashflow-forecast' : 'cashflow-forecasting-integration',
              //   enabled: enableCashFlowForecasting
              // }
            ]
          },
          {
            heading: 'Funding',
            items: [
              {
                label: 'Funding options',
                icon: 'manage_search',
                routeName: 'loan-matches',
                enabled: hasLoanProducts
              },
              {
                label: 'Applications',
                icon: 'description',
                routeName: 'my-offers',
                alertCount: this.applicationsNeedingAttention,
                enabled: true
              },
              {
                label: 'Other funding types',
                flyoutLabel: 'Other funding',
                icon: 'layers',
                enabled: true,
                expanded: !this.isMobileViewport,
                menuItems: [
                  {
                    label: 'Grant funding',
                    icon: 'redeem',
                    productName: 'grants',
                    routeName: 'grant-matches',
                    enabled: hasGrantProducts
                  },
                  {
                    label: 'R&D funding',
                    icon: 'science',
                    routeName: 'research-and-development',
                    enabled: countryCode === 'GB' && enableResearchAndDevelopmentPage,
                    productName: 'randd'
                  },
                  {
                    label: 'Credit cards',
                    icon: 'credit_card',
                    routeName: 'credit-cards',
                    enabled: countryCode === 'GB' && enableCreditCardPage && hasValidRegistrationNumber(this.company?.registrationNumber),
                    productName: 'creditcards'
                  },
                  {
                    label: 'Equity funding',
                    icon: 'group_add',
                    productName: 'equity',
                    routeName: 'equity-matches',
                    enabled: hasEquityProducts
                  }
                ]
              }
            ]
          },
          {
            heading: 'Business insights',
            items: [
              {
                label: 'Credit score',
                icon: 'credit_score',
                routeName: 'credit-safe-report',
                enabled: hasCreditSafeReport && enableCreditsafeReportPage
              },
              {
                label: 'Financial insights',
                icon: 'monitoring',
                enabled: true,
                expanded: !this.isMobileViewport,
                menuItems: [
                  {
                    label: 'Accounting insights',
                    icon: 'insights',
                    routeName: 'accounting-insights',
                    enabled: true
                  },
                  {
                    label: 'Banking insights',
                    icon: 'account_balance',
                    routeName: 'banking-insights',
                    enabled: true
                  },
                  {
                    label: 'Credit score improvements',
                    icon: 'trending_up',
                    routeName: 'credit-score-improvements',
                    enabled: enableCreditScoreImprovementsPage
                  },
                  {
                    label: 'Credit affordability',
                    icon: 'speed',
                    routeName: 'credit-health-check',
                    enabled: enableCreditHealthCheck
                  }
                ]
              },
              {
                label: 'Documents',
                icon: 'folder',
                routeName: 'data-room',
                enabled: true
              },
              {
                label: 'Connected data',
                routeName: 'integrations',
                icon: 'hub',
                enabled: enableIllionIntegration || enableAccountingSoftwareIntegration || enableOpenBankingIntegration || enablePlaidIntegration
              }
            ]
          },
          {
            heading: 'Save money',
            items: [
              {
                label: 'Spending insights',
                icon: 'savings',
                enabled: true,
                menuItems: [
                  {
                    label: 'R&D tax credits',
                    icon: 'science',
                    routeName: 'research-and-development',
                    enabled: countryCode === 'US' && enableResearchAndDevelopmentPage
                  },
                  {
                    label: 'Business insurance',
                    icon: 'shield',
                    routeName: 'business-insurance',
                    enabled: enableBusinessInsurancePage
                  },
                  {
                    label: 'Energy savings',
                    icon: 'bolt',
                    routeName: 'utilities',
                    enabled: enableSavingsFeatures && enableUtilitiesPage
                  },
                  {
                    label: 'Foreign exchange',
                    icon: 'currency_exchange',
                    routeName: 'foreign-exchange',
                    enabled: enableFXPage
                  },
                  {
                    label: 'Pensions',
                    icon: 'elderly',
                    routeName: 'pension',
                    enabled: enablePensionPage
                  }
                ]
              },
              {
                label: 'Banking',
                icon: 'account_balance',
                enabled: true,
                menuItems: [
                  {
                    label: 'Business savings accounts',
                    icon: 'savings',
                    routeName: 'business-savings-accounts',
                    enabled: enableBusinessSavingsAccountsPage
                  },
                  {
                    label: 'Compare bank accounts',
                    icon: 'compare_arrows',
                    externalLink: 'https://swoopfunding.com/compare-business-current-account/?prev_page=app_BCA',
                    enabled: enableSavingsFeatures && enableBankingPage
                  },
                  {
                    label: countryCode === 'CA' ? 'Business chequing accounts' : 'Business checking accounts',
                    icon: 'account_balance',
                    routeName: 'business-checking-accounts',
                    enabled: enableBusinessCheckingAccountsPage
                  },
                  {
                    label: 'Business credit cards',
                    icon: 'credit_card',
                    routeName: 'business-credit-cards',
                    enabled: countryCode !== 'GB' && enableCreditCardPage
                  }
                ]
              }
            ]
          }
        ]
      }
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/styles/swoop/_variables.scss';
@import '@/assets/styles/swoop/_mixins.scss';

$sidebar-rail-width: 72px;
$sidebar-expanded-width: 268px;
$sidebar-divider-color: rgba(255, 255, 255, 0.1);
$sidebar-ink: rgba(255, 255, 255, 0.8);
$sidebar-ink-faint: rgba(255, 255, 255, 0.55);
$sidebar-hover: rgba(255, 255, 255, 0.07);
$sidebar-selected-text: var(--color-secondary-200);
$sidebar-selected-fill: color-mix(in srgb, var(--color-secondary-500) 26%, transparent);

.sidebar-wrapper {
  width: $sidebar-expanded-width;
  flex-shrink: 0;
  transition: width 0.25s ease;
  letter-spacing: -0.004em;

  &.mobile-sidebar-closed {
    @media only screen and (max-width: $breakpoint-sm-max) {
      display: none;
    }
  }

  &.mobile-sidebar-open {
    @media only screen and (max-width: $breakpoint-sm-max) {
      position: fixed;
      top: 0;
      left: 0;
      z-index: 99;
      height: 100%;
      width: 100%;
    }
  }

  .mobile-drawer-backdrop {
    display: none;

    @media only screen and (max-width: $breakpoint-sm-max) {
      display: block;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.45);
      z-index: 58;
    }
  }

  aside {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100vh;
    position: sticky;
    top: 0;
    z-index: 60;
    background-color: var(--color-primary-600);
    color: var(--color-white);
    overflow: hidden;

    @media only screen and (max-width: $breakpoint-sm-max) {
      position: fixed;
      top: 0;
      left: 0;
      width: calc(100% - 54px);
      max-width: 400px;
      box-shadow: 2px 0 12px rgba(0, 0, 0, 0.25);
    }
  }

  &.is-retracted {
    @media only screen and (min-width: $breakpoint-md) {
      width: $sidebar-rail-width;

      aside {
        & {
          .menu-item-text,
          .badge,
          .sidebar-dropdown-content-container,
          .integrations-menu-item,
          .menu-category-item > .chevron,
          .toggle-btn,
          .user-row .user-name,
          .user-row .user-row-gear,
          .user-menu-item .user-menu-label {
            display: none !important;
          }

          // Hovering the logo swaps the brand mark for the control that reopens the menu.
          .logo-area:hover {
            .swoop-monogram,
            .monogram-circle {
              display: none;
            }

            .logo-expand-icon {
              display: flex;
            }
          }

          .sidebar-footer .user-row .user-avatar {
            width: 36px;
            height: 36px;
          }

          .sidebar-footer .user-menu-items {
            align-items: center;
            gap: $spacer-xs-4px;

            .user-menu-item {
              width: 36px;
              min-width: 36px;
              height: 36px;
              padding: 0;
              gap: 0;
              border-radius: 50%;
              justify-content: center;
            }
          }

          .menu {
            padding-left: 12px;
            padding-right: 12px;

            .menu-section-heading {
              height: 1px;
              margin: 12px 4px;
              padding: 0;
              font-size: 0;
              overflow: hidden;
              background-color: $sidebar-divider-color;
            }
          }

          .menu-item-content {
            justify-content: center;
            padding: 0;
            position: relative;
          }

          .alert-badge {
            position: absolute;
            top: 3px;
            right: 3px;
            min-width: 16px;
            height: 16px;
            border-radius: 999px;
            font-size: 10px;
            line-height: 16px;
          }

          .menu-route-item.border {
            flex-direction: row;
            margin: 0;
            padding: 0;
            border-top: 0 !important;
            border-bottom: 0 !important;
          }

          // Centre the monogram inside the narrow rail (toggle is hidden via display:none above)
          .sidebar-header {
            justify-content: center;
            padding-left: 8px;
            padding-right: 8px;

            .logo-area {
              justify-content: center;
              flex: 0 0 auto;
              cursor: pointer;
            }
          }

          .sidebar-footer .user-row {
            justify-content: center;
            padding: 8px 0;
          }

          .sidebar-footer .user-row.has-active-route {
            background-color: $sidebar-selected-fill;
          }
        }
      }
    }
  }

  .sidebar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 16px;
    min-height: 64px;
    flex-shrink: 0;
    box-sizing: border-box;
    gap: 12px;

    .logo-area {
      display: flex;
      align-items: center;
      overflow: hidden;
      min-width: 0;
      flex: 1 1 auto;

      &:focus-visible {
        outline: 1px solid var(--color-white);
        outline-offset: 2px;
        border-radius: 8px;
      }

      .swoop-logo-container {
        display: flex;
      }

      .swoop-logo {
        max-width: 140px;
        max-height: 28px;
        width: auto;
        height: auto;
        margin: 0;
        object-fit: contain;
      }

      .monogram-circle {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background-color: var(--color-secondary);
        color: var(--color-primary-button-text);
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        font-size: 16px;
        user-select: none;
        flex-shrink: 0;
      }

      .logo-expand-icon {
        display: none;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background-color: var(--color-white);
        color: var(--color-primary-600);
        align-items: center;
        justify-content: center;
        font-size: 20px;
        flex-shrink: 0;
      }

      .swoop-monogram {
        width: 36px;
        height: 36px;
        flex-shrink: 0;
        display: block;
      }
    }

    .toggle-btn {
      background: transparent;
      border: 0;
      color: var(--color-white);
      cursor: pointer;
      padding: 4px;
      border-radius: 8px;

      &:focus-visible {
        outline: 1px solid var(--color-white);
        outline-offset: 0;
      }
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
      margin-left: auto;

      .toggle-icon {
        font-size: 22px;
        color: var(--color-white);
      }
    }
  }

  .sidebar-divider {
    height: 1px;
    background-color: $sidebar-divider-color;
    margin: 0;
  }

  // Nav menu (scrollable middle area)
  aside .menu {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 8px 14px 12px;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      width: 0;
      height: 0;
    }

    .menu-section-heading {
      padding: 18px 14px 8px;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.09em;
      text-transform: uppercase;
      color: rgba(255, 255, 255, 0.42);
      white-space: nowrap;
    }
  }

  aside .sidebar-footer {
    flex-shrink: 0;
    padding: 8px 12px 9px;
    border-top: 1px solid $sidebar-divider-color;
    display: flex;
    flex-direction: column;
    gap: 0;

    .user-row {
      display: flex;
      align-items: center;
      gap: 12px;
      height: 40px;
      padding: 0 6px;
      border-radius: 8px;
      cursor: pointer;

      .user-avatar,
      .user-name,
      .user-row-gear {
        pointer-events: none;
      }

      .user-avatar {
        width: 30px;
        height: 30px;
        border-radius: 50%;
        background-color: var(--color-secondary-500);
        color: var(--color-white);
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        font-size: 12px;
        flex-shrink: 0;
        user-select: none;
        overflow: hidden;
      }

      .user-name {
        flex: 1;
        min-width: 0;
        font-size: 13.5px;
        font-weight: 600;
        color: var(--color-white);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .user-row-gear {
        display: flex;
        align-items: center;
        justify-content: center;

        .material-icons-outlined {
          font-size: 18px;
          font-weight: 400;
          color: $sidebar-ink-faint;
        }
      }

      &:focus-visible {
        outline: 1px solid var(--color-white);
        outline-offset: -1px;
      }

      &.is-menu-open .user-row-gear .material-icons-outlined {
        color: $sidebar-selected-text;
      }
    }

    .user-menu-items {
      display: flex;
      flex-direction: column;
      gap: 2px;
      overflow: hidden;

      .user-menu-item {
        background: transparent;
        border: 0;
        color: $sidebar-ink;
        font-size: 15px;
        font-weight: 400;
        text-align: left;
        margin: 0;
        height: 44px;
        padding: 0 14px;
        border-radius: 8px;
        text-decoration: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 12px;
        font-family: inherit;
        transition: background-color 0.12s ease, color 0.12s ease;

        &:hover {
          text-decoration: none;
          background-color: $sidebar-hover;
          color: var(--color-white);

          .material-symbols-outlined {
            font-variation-settings: 'FILL' 1;
          }
        }

        &:focus-visible {
          outline: 1px solid var(--color-white);
          outline-offset: -1px;
        }

        &.router-link-active,
        &.router-link-active:hover,
        &.router-link-active:focus {
          background-color: $sidebar-selected-fill;
          box-shadow: inset 3px 0 0 var(--color-secondary-500);
          color: $sidebar-selected-text;
          font-weight: 700;
        }

        &.logout,
        &.logout .material-icons-outlined {
          color: var(--color-error-300);
        }

        .material-icons-outlined {
          font-size: 22px;
        }
      }
    }
  }

  .user-menu-slide-enter-active,
  .user-menu-slide-leave-active {
    transition: max-height 0.2s ease, opacity 0.15s ease;
    overflow: hidden;
  }

  .user-menu-slide-enter-from,
  .user-menu-slide-leave-to {
    max-height: 0;
    opacity: 0;
  }

  .user-menu-slide-enter-to,
  .user-menu-slide-leave-from {
    max-height: 260px;
    opacity: 1;
  }

  .user-menu-slide-enter,
  .user-menu-slide-leave-to {
    max-height: 0;
    opacity: 0;
  }
}

.sidebar-wrapper .sidebar-flyout {
  position: fixed;
  z-index: 1000;
  width: 276px;
  padding: $spacer-md-16px;
  border-radius: 8px;
  background-color: var(--color-primary-700);
  box-shadow: 0 13px 18.5px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: $spacer-xs-4px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: -$spacer-sm-8px;
    width: $spacer-sm-8px;
  }

  .sidebar-flyout-heading {
    margin: 0 0 $spacer-xs-4px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 1.08px;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.55);
  }

  @media only screen and (max-width: $breakpoint-sm-max) {
    display: none !important;
  }
}

.sidebar-wrapper .sidebar-tooltip {
  position: fixed;
  background-color: var(--color-primary-700);
  color: var(--color-white);
  font-size: 14px;
  font-weight: 700;
  padding: 8px 12px;
  border-radius: 8px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 1000;
  transform: translateY(-50%);
  box-shadow: 0 9px 18px rgba(0, 0, 0, 0.15);

  &::before {
    content: '';
    position: absolute;
    left: -5px;
    top: 50%;
    width: 10px;
    height: 10px;
    border-radius: 2px;
    background-color: inherit;
    transform: translateY(-50%) rotate(45deg);
  }

  @media only screen and (max-width: $breakpoint-sm-max) {
    display: none !important;
  }
}

.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: opacity 0.12s ease;
}
.tooltip-fade-enter-from,
.tooltip-fade-leave-to,
.tooltip-fade-enter,
.tooltip-fade-leave-to {
  opacity: 0;
}
</style>
